'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { patientLogin } from '@/app/actions/patientLogin'
import Button from '@/components/ui/CustomButton'
import Input from '@/components/ui/CustomInput'
import { Form, FormControl, FormField, FormItem, FormMessage, FormLabel } from '@/components/ui/form'
import { useToast } from '@/components/ui/use-toast'

import { Checkbox } from '@/components/ui/checkbox'
import { Eye, EyeOff } from 'lucide-react'
import { useEffect, useState } from 'react'
import useAuthStore from '../store'

const REMEMBERED_LOGIN_KEY = 'gowello_patient_remembered_login'

// Define the schema using Zod
const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

// Infer the type from the schema
type LoginFormInputs = z.infer<typeof loginSchema>

const LoginForm = () => {
  const { setEmail, setUser, setOtp, setState, setPhoneNumber } = useAuthStore()
  const { toast } = useToast()
  const [showPassword, setShowPassword] = useState(false)
  const [rememberPassword, setRememberPassword] = useState(false)

  const form = useForm<LoginFormInputs>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  useEffect(() => {
    const rememberedLogin = localStorage.getItem(REMEMBERED_LOGIN_KEY)

    if (!rememberedLogin) {
      return
    }

    try {
      const parsedLogin = loginSchema.parse(JSON.parse(rememberedLogin))

      form.reset(parsedLogin)
      setRememberPassword(true)
    } catch (error) {
      localStorage.removeItem(REMEMBERED_LOGIN_KEY)
    }
  }, [form])

  const onSubmit = async (data: LoginFormInputs) => {
    try {
      const res = await patientLogin({
        email: data.email.toLowerCase(),
        password: data.password,
      })
      if (res?.error) {
        toast({
          title: 'Error',
          description: res.error,
          variant: 'error',
        })
      }
      if (res?.success) {
        toast({
          title: 'Success',
          description: 'OTP sent to your number',
          variant: 'success',
        })
        if (rememberPassword) {
          localStorage.setItem(
            REMEMBERED_LOGIN_KEY,
            JSON.stringify({
              email: data.email.toLowerCase(),
              password: data.password,
            })
          )
        } else {
          localStorage.removeItem(REMEMBERED_LOGIN_KEY)
        }
        setEmail(data.email.toLowerCase())
        setUser(res.data.user)
        setOtp(res.data.otp)
        setPhoneNumber(res.data.phone_number)
        setState('otp')
      }
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description: 'Something went wrong',
        variant: 'error',
      })
    }
  }

  return (
    <div
      className={`
        grid gap-2

        lg:gap-4
      `}
    >
      <h1
        className={`
          text-2xl font-medium

          lg:text-4xl

          md:text-3xl lg:ml-24 mb-3
        `}
      >
        Patient Log In
      </h1>
      {/* <p className='font-[700]'>Enter your credentials to access your account</p> */}
      <div className='flex h-full w-full flex-col items-center justify-center'>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className={`mt- flex w-full max-w-md flex-col gap-4`}
          >
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>User Name <span className='text-[#EB0808]'>*</span></FormLabel>
                  <FormControl>
                    <Input placeholder='Enter Username' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password <span className='text-[#EB0808]'>*</span></FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder='Enter Password'
                        {...field}
                        className="pr-10"
                      />

                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                      >
                        {showPassword ? <EyeOff size={18} strokeWidth={1.5} className='text-[#F59405]'/> : <Eye size={18} strokeWidth={1.5} className='text-[#F59405]'/>}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <label htmlFor='remember-password' className='flex cursor-pointer items-center gap-2'>
              <Checkbox
                id='remember-password'
                checked={rememberPassword}
                onCheckedChange={(checked) => {
                  const shouldRemember = checked === true

                  setRememberPassword(shouldRemember)

                  if (!shouldRemember) {
                    localStorage.removeItem(REMEMBERED_LOGIN_KEY)
                  }
                }}
                className='h-4 w-4 border-2 border-black text-black font-bold focus:ring-black'
              />
              <span className='text-base font-light'>Remember Password</span>
            </label>

            <div className='mt-3'>
              <Button
                disabled={form.formState.isSubmitting}
                loading={form.formState.isSubmitting}
                label={'Log In'}
                type='submit'
              />
            </div>

            <div className='flex w-full  justify-center mt-1'>
              <p className='text-lg font-medium text-text-gray'></p>
              <Link
                href='/auth/forgot-password'
                className={`
                  font-semibold text-black text-base

                  
                `}
                replace
              >
                Forgot Your Password?
              </Link>
            </div>
          </form>
        </Form>
        <span
          className={`
            mt-3 flex w-full flex-row justify-center text-center text-base
            font-normal text-black whitespace-nowrap
          `}
        >
          Don&apos;t have an account?
          <Link
            href='/auth/signup'
            className={`
              ml-2 text-primary-yellow

              hover:text-[#E58904]
            `}
            replace
          >
            Create an account?
          </Link>
        </span>
      </div>
    </div>
  )
}

export default LoginForm
